import React, { useState } from 'react';
import { Upload, Mail, Phone, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://apiproblema.digitalbotia.com.br/api';

function ClientForm() {
  const [formData, setFormData] = useState({
    email: '',
    telefone: '',
    comprovante: null
  });
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError('Por favor, envie apenas imagens (JPEG, PNG) ou PDF.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('O arquivo deve ter no máximo 10MB.');
        return;
      }
      setFormData(prev => ({
        ...prev,
        comprovante: file
      }));
      setFileName(file.name);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!formData.email || !formData.telefone || !formData.comprovante) {
      setError('Por favor, preencha todos os campos.');
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();
      data.append('email', formData.email);
      data.append('telefone', formData.telefone);
      data.append('comprovante', formData.comprovante);

      const response = await axios.post(`${API_URL}/submit`, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccess(true);
        setFormData({
          email: '',
          telefone: '',
          comprovante: null
        });
        setFileName('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao enviar solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Solicitação Enviada!
          </h2>
          <p className="text-gray-600 mb-8">
            Aguarde que sua licença será liberada novamente em breve. 
            Entraremos em contato através do email fornecido.
          </p>
          <button
            onClick={() => setSuccess(false)}
            className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-200"
          >
            Enviar Nova Solicitação
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Restauração de Licença
          </h1>
          <p className="text-gray-600">
            <span className="text-green-600 font-bold text-lg">OpenBot</span> - Preencha os dados abaixo
          </p>
        </div>

        <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800 mb-1">Aviso Importante</h3>
              <p className="text-sm text-yellow-700">
                Houve uma atualização no dia <strong>04/12/2025</strong> no sistema de licenças e alguns usuários foram afetados. 
                Pedimos desculpas pelo transtorno e estamos trabalhando para restaurar todas as licenças o mais rápido possível.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email de Licença
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                placeholder="seu@email.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Número de Telefone
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="tel"
                name="telefone"
                value={formData.telefone}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                placeholder="(00) 00000-0000"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comprovante de Pagamento
            </label>
            <div className="relative">
              <input
                type="file"
                id="comprovante"
                onChange={handleFileChange}
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                className="hidden"
                required
              />
              <label
                htmlFor="comprovante"
                className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 transition-all"
              >
                <Upload className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-gray-600">
                  {fileName || 'Selecione uma imagem ou PDF'}
                </span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Formatos aceitos: JPEG, PNG, PDF (máx. 10MB)
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Enviando...' : 'Enviar Solicitação'}
          </button>
        </form>

      </div>
    </div>
  );
}

export default ClientForm;
